import {Component, HostBinding, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { Subscription } from 'rxjs';
import { ReportPageInterface } from 'src/app/interface';
import { FieldValue, FieldValueReset, Franchise, Naics, Report, ReportVersion, SelectorItem } from 'src/app/model';
import { BusinessPurpose, EntityType, FooterButtonOption, PageNumbers, TransactionType } from 'src/app/model/Enums';
import { ReportProvider } from 'src/app/providers';
import { ReportService, ToasterService } from 'src/app/services';
import { radioGroupOption } from '../../commonModel/common-classes';
import { ReportPageConstant } from 'src/app/constants/reportPage.constants';
import {routeTransition} from '../../../../animations/route-transition';

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  animations: [routeTransition],
})
export class OverviewComponent implements OnInit, OnDestroy, ReportPageInterface {
  @HostBinding('@routeTransition') routeAnimation = true;
  @ViewChild('overviewForm') overviewForm: NgForm;

  public currentReport: Report;
  private reportSub: Subscription;
  reportPageConst = ReportPageConstant;

  //#region "industry-name"
  naicsList: Array<Naics> = [];
  naicsAutoOpen: boolean = false;
  naicsSelIndex: number = null;
  naicsError: boolean = false;
  naicsCanError: boolean = false;
  isEllipsis: boolean = true;
  @ViewChild('selectorNAICS') selectorNAICS;
  //#endregion

  //#region "company-dba"
  isSameAsBusinessName: boolean = false;
  //#endregion

  //#region "business-entity"
  entityTypeValues = [
    new SelectorItem('Form 1065', EntityType.FORM_1065, [
      new SelectorItem('Limited Liability Company', 'FORM_1065', null),
      new SelectorItem('Professional Limited Liability Company', 'FORM_1065', null),
      new SelectorItem('Professional Association', 'FORM_1065', null),
      new SelectorItem('General Partnership', 'FORM_1065', null)
    ]),
    new SelectorItem('Form 1120S', EntityType.FORM_1120S, [
      new SelectorItem('S Corporation', 'FORM_1120S', null),
      new SelectorItem('LLC Filing as an S Corporation', 'FORM_1120S', null)
    ]),
    new SelectorItem('Form 1120', EntityType.FORM_1120, [
      new SelectorItem('C Corporation', 'FORM_1120', null)
    ]),
    new SelectorItem('Schedule C', EntityType.SCHEDULE_C, [
      new SelectorItem('Sole Proprietorship', 'SCHEDULE_C', null),
      new SelectorItem('Single Member LLC (Disregarded Entity)', 'SCHEDULE_C', null)
    ])
  ];
  //#endregion

  //#region "business-purpose"
  businessPurposeEnum = BusinessPurpose;
  businessPurposeValues: Array<SelectorItem> = [
    new SelectorItem('Internal Valuation (for SBA Lenders)', BusinessPurpose.FINANCIAL_LENDING, null),
    new SelectorItem('Price Sanity Check (for a Business Broker)', BusinessPurpose.PURCHASE_OR_SALE_BUSINESS, null),
    new SelectorItem('Verify Asking Price (for a Buyer)', BusinessPurpose.VERIFY_ASKING_PRICE, null),
    new SelectorItem('Determine Reasonable Sale Price (for a Seller)', BusinessPurpose.DETERMINE_SALE_PRICE, null),
    new SelectorItem('Estate/Tax Planning', BusinessPurpose.TAX_PLANNING, null),
    new SelectorItem('Litigation or Divorce', BusinessPurpose.MARITAL_DISSOLUTION, null),
    new SelectorItem('Other', BusinessPurpose.OTHER, null),
  ];
  //#endregion

  //#region "franchise"
  franchiseList: Array<Franchise> = [];
  franchiseAutoOpen: boolean = false;
  franchiseError: boolean = false;
  franchiseOption: radioGroupOption[] = [];
  franchiseSelIndex: number = null;
  //#endregion

  // #region "transaction-type"
  transactionListForFinancialLending: radioGroupOption[] = [];
  transactionListForPurchaseOrSaleBusiness: radioGroupOption[] = [];
  transactionTypeEnum = TransactionType;
  //#endregion

  constructor(private reportService: ReportService,
    private reportProvider: ReportProvider,
    private router: Router,
    private toastr: ToasterService,
    private scrollToService: ScrollToService) { }

  ngOnInit(): void {
    this.franchiseOption.push({ title: 'Yes', value: true });
    this.franchiseOption.push({ title: 'No', value: false });

    this.transactionListForFinancialLending.push({ title: '100% Change of Ownership', value: TransactionType.CHANGE_OWNERSHIP });
    this.transactionListForFinancialLending.push({ title: 'Partner Buyout', value: TransactionType.BUYOUT });
    this.transactionListForFinancialLending.push({ title: 'Debt Refinance', value: TransactionType.REFINANCING });
    this.transactionListForFinancialLending.push({ title: 'Other Collateral Purposes', value: TransactionType.OTHER });

    this.transactionListForPurchaseOrSaleBusiness.push({ title: 'Third Party Buyout', value: TransactionType.THIRD_PARTY_BUYOUT });
    this.transactionListForPurchaseOrSaleBusiness.push({ title: 'Partner Buyout', value: TransactionType.PARTNER_BUYOUT });

    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
      if (this.currentReport && !this.currentReport.LATEST) {
        this.currentReport.LATEST = new ReportVersion();
      }
      if (this.currentReport?.LATEST?.COMPANY) {
        if (this.currentReport.LATEST.COMPANY.NAICS) {
          this.currentReport.LATEST.COMPANY.NAICS_NAME = this.currentReport.LATEST.COMPANY.NAICS.CODE + " - " + this.currentReport.LATEST.COMPANY.NAICS.NAICS_DESC;
        }
        if (this.currentReport.LATEST.COMPANY.NAME) {
          this.checkNameForSame();
        }
        if (this.currentReport.LATEST.COMPANY.FRANCHISE) {
          this.currentReport.LATEST.COMPANY.FRANCHISE_NAME = this.currentReport.LATEST.COMPANY.FRANCHISE.FRANCHISE_NAME;
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.reportSub)
      this.reportSub.unsubscribe();
  }

  /**
   * @description for scroll focuse on inputs
   * @param $event key down
   */
  @HostListener('window:keyup', ['$event'])
  onKeyPress($event: KeyboardEvent) {
    if ($event.keyCode == 9) {
      const elePosition = document.activeElement.getBoundingClientRect();
      if (window.innerHeight - 115 < elePosition.y) {
        document.activeElement.scrollIntoView(true);
      }
    }
  }

  /**
   * @description field update API
   */
  callUpdateAPI(fieldGroup: string, fieldName: string) {

    setTimeout(() => {
      if (fieldGroup == 'COMPANY' && (this.currentReport.LATEST.COMPANY[fieldName] != null && this.currentReport.LATEST.COMPANY[fieldName] != '')) {
        this.autoScroll(fieldName);
      } else if (this.currentReport.LATEST[fieldName] != null && this.currentReport.LATEST[fieldName] != '') {
        this.autoScroll(fieldName);
      }
    }, 10);

    if (fieldName == 'BUSINESS_PURPOSE' && this.currentReport?.LATEST?.COMPANY?.TRANSACTION_TYPE) {
      this.currentReport.LATEST.COMPANY.TRANSACTION_TYPE = null;
      this.resetFieldAPI('COMPANY', ['TRANSACTION_TYPE']);
    }

    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = fieldGroup; // 'REPORT_VERSION';
    objFieldValue.FIELD = fieldName; // 'BORROWER_NAME';
    if (fieldGroup == 'COMPANY') {
      objFieldValue.VALUE = this.currentReport.LATEST.COMPANY[fieldName];
    } else {
      objFieldValue.VALUE = this.currentReport.LATEST[fieldName];
    }
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (res.success) {
        if (fieldName == 'IS_FRANCHISE' && !this.currentReport.LATEST.COMPANY.IS_FRANCHISE && this.currentReport.LATEST.COMPANY.FRANCHISE) {
          this.resetFranchise();
        }
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  autoScroll(fieldName: string) {
    switch (fieldName) {
      case 'NAME':
        this.reportService.makeElementFocus('DBA');
        break;
      case 'DBA':
        if (!document.activeElement || document.activeElement.id != 'companyName') {
          if (this.currentReport.LATEST.COMPANY.NAICS) {
            this.reportService.makeElementFocus('overview-reset-company-naics');
          } else {
            this.reportService.makeElementFocus('naicsCode');
          }
        }
        break;
      case 'ENTITY_TYPE':
        if (!document.activeElement || (document.activeElement.id != 'overview-reset-company-naics' && document.activeElement.id != 'naicsCode')) {
          this.reportService.makeElementFocus('overview-company-franchise-radio-group-yes');
        }
        break;
      case 'IS_FRANCHISE':
        if (!document.activeElement || document.activeElement.id != 'overview-company-franchise-radio-group-yes') {
          if (this.currentReport.LATEST.COMPANY.IS_FRANCHISE) {
            setTimeout(() => {
              if (this.currentReport.LATEST.COMPANY.FRANCHISE_NAME) {
                this.reportService.makeElementFocus('overview-reset-franchise-name');
              } else {
                this.reportService.makeElementFocus('FRANCHISE_NAME');
              }
            }, 10);
          } else {
            this.reportService.makeElementFocus('businessPurpose');
          }
        }
        break;
      case 'BUSINESS_PURPOSE':
        setTimeout(() => {
          if (!document.activeElement || (document.activeElement.id != 'overview-reset-franchise-name' && document.activeElement.id != 'FRANCHISE_NAME' && document.activeElement.id != 'businessPurpose')) {
            if (this.currentReport.LATEST.COMPANY.BUSINESS_PURPOSE == BusinessPurpose.FINANCIAL_LENDING) {
              this.reportService.makeElementFocus('overview-transaction-type-radio-group-100%-change-of-ownership');
            } else if ((this.currentReport.LATEST.COMPANY.BUSINESS_PURPOSE == BusinessPurpose.OTHER)) {
              this.reportService.makeElementFocus('OTHER_BUSINESS_PURPOSE');
            }
          }
        }, 10);
        break;
    }
  }

  /**
 * @description for reset fields
 */
  resetFieldAPI(fieldGroup: string, fieldNameList: Array<string>) {
    let objFieldValueReset = new FieldValueReset();
    objFieldValueReset.GROUP = fieldGroup;
    objFieldValueReset.FIELD_LIST = fieldNameList;
    this.reportProvider.resetReportField(this.currentReport.LATEST.ID, objFieldValueReset).subscribe(res => {
      if (!res.success) {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  //#region "industry-name"

  /**
   * Naics Selector
   * */
  autocompleteNaics(value: string,): void {
    // Close AutoComplete
    this.naicsAutoOpen = false;
    // Clear Current Franchise Object
    this.currentReport.LATEST.COMPANY.NAICS = null;
    this.naicsSelIndex = null;

    if (!value)
      return;

    if (value.length > 0) {
      this.reportProvider.getNaics(1, 100, value).subscribe(
        data => {
          if (data.success) {
            this.naicsList = data.result.items;
            // Open AutoComplete
            this.naicsAutoOpen = true;
          }
          else console.error("Failed to get Naics")
        }, err => {
          // Error Handling
        });
    } else this.naicsList = []
  }

  /**
   * @description for select naics
   * @param naics selected naics
   * @param timeout is timeout
   */
  selectNaics(naics: Naics, timeout: boolean = false): void {
    this.naicsError = false;
    this.naicsSelIndex = null;
    this.naicsCanError = !naics.CAN_VALUE;

    this.currentReport.LATEST.COMPANY.NAICS = null;
    this.currentReport.LATEST.COMPANY.NAICS_NAME = null;

    if (naics.CAN_VALUE) {
      this.naicsList = [];
      this.naicsAutoOpen = false;
      this.currentReport.LATEST.COMPANY.NAICS = naics;
      if (timeout) {
        setTimeout(() => {
          this.currentReport.LATEST.COMPANY.NAICS_NAME = naics.CODE + " - " + naics.NAICS_DESC;
          this.callUpdateNaicsAPI();
        }, 150);
      } else {
        this.currentReport.LATEST.COMPANY.NAICS_NAME = naics.CODE + " - " + naics.NAICS_DESC;
        this.callUpdateNaicsAPI();
      }
    } else {
      this.toastr.info('Valzy does not support this NAICS code.');
    }

  }

  /**
   * @description for focus lost of dropdown
   */
  naicsFocusLost(): void {
    this.naicsError = !this.currentReport.LATEST.COMPANY.NAICS;
  }

  @HostListener('document:click', ['$event.target'])
  clickedOutside(targetElement){
    const clickedInside = this.selectorNAICS?.nativeElement?.contains(targetElement);
    if (!clickedInside) {
      this.naicsAutoOpen = false;
    }
  }

  /**
   * @description reset naics
   */
  resetNaics(): void {
    this.currentReport.LATEST.COMPANY.NAICS = null;
    this.currentReport.LATEST.COMPANY.NAICS_NAME = null;
    this.naicsCanError = false;
    this.naicsSelIndex = null;
    this.resetFieldAPI('COMPANY', ['NAICS']);
    this.reportService.updateMaxStage(PageNumbers.OVERVIEW, true);
    document.getElementById('naicsCode').focus();
    this.updateRiskFields();
  }

  updateRiskFields() {
    if (this.currentReport.LATEST?.RISK?.ID) {
      let objFieldValue = new FieldValueReset();
      objFieldValue.GROUP = 'RISK';
      objFieldValue.FIELD_LIST = [
        'DIV_CUSTOMERS',
        'DIV_SINGLE_CLIENT_PERCENT',
        'DIV_MORE_FIFTY',
        'DIV_SALES_PERCENT',
        'DIV_CONTRACTS',
        'DIV_SALES_LOST',
        'DIV_VENDOR_PERCENT',
        'DIV_SIMILAR_VENDOR',
        'PG_OWNER_LEAVE',
        'BE_LICENSES',
        'BE_LICENSES_REQ',
        'RR_IS_RECURRING',
        'RR_RECURRING_AMOUNT',
        'OPT_DV',
        'OPT_PG',
        'OPT_BE',
        'OPT_RR'
      ];
      this.reportProvider.resetReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
        if (res.success) {
          this.currentReport.LATEST.RISK.DIV_CUSTOMERS = null;
          this.currentReport.LATEST.RISK.DIV_SINGLE_CLIENT_PERCENT = null;
          this.currentReport.LATEST.RISK.DIV_MORE_FIFTY = null;
          this.currentReport.LATEST.RISK.DIV_SALES_PERCENT = null;
          this.currentReport.LATEST.RISK.DIV_CONTRACTS = null;
          this.currentReport.LATEST.RISK.DIV_SALES_LOST = null;
          this.currentReport.LATEST.RISK.DIV_VENDOR_PERCENT = null;
          this.currentReport.LATEST.RISK.DIV_SIMILAR_VENDOR = null;
          this.currentReport.LATEST.RISK.PG_OWNER_LEAVE = null;
          this.currentReport.LATEST.RISK.BE_LICENSES = null;
          this.currentReport.LATEST.RISK.BE_LICENSES_REQ = null;
          this.currentReport.LATEST.RISK.RR_IS_RECURRING = null;
          this.currentReport.LATEST.RISK.RR_RECURRING_AMOUNT = null;
          this.currentReport.LATEST.RISK.OPT_DV = null;
          this.currentReport.LATEST.RISK.OPT_PG = null;
          this.currentReport.LATEST.RISK.OPT_BE = null;
          this.currentReport.LATEST.RISK.OPT_RR = null;
        } else {
          this.toastr.error(res.error.txt);
        }
      }, err => {
        // Error Handling
      });
    }
  }

  /**
   * @description on tab press of dropdown
   * @param event tab press event
   */
  naicsTabPress(event: any) {
    if (this.naicsList)
      if (this.naicsList.length > 0) {
        if (this.naicsSelIndex)
          this.selectNaics(this.naicsList[this.naicsSelIndex]);
        else this.selectNaics(this.naicsList[0]);
      }
  }

  /**
   * @description for pick naics
   * @param event click event
   */
  pickNaicsFromList(event: any): void {
    event.preventDefault();

    if (this.naicsSelIndex !== null && this.naicsList) {
      if (this.naicsList[this.naicsSelIndex]) {
        this.selectNaics(this.naicsList[this.naicsSelIndex], true);
      }
    }
  }

  /**
   * @description navigate up in dropdown
   * @param event key up event
   * @returns selected naics
   */
  naicsArrowUp(event: any): void {
    event.preventDefault();

    if (this.naicsList) {
      if (this.naicsList.length > 0) {

        // If its last upper item, clear
        if (this.naicsSelIndex === 0) {
          this.naicsSelIndex = null;
          this.naicsList[0].isOver = false;
          return;
        }

        // if its not the first run
        if (this.naicsSelIndex !== null) {
          // Increment Index
          this.naicsSelIndex--;

          // 'Over' NAICS, clear rest
          for (let i = 0; i < this.naicsList.length; i++) {
            this.naicsList[i].isOver = this.naicsSelIndex === i;
          }

          if (this.naicsList[this.naicsSelIndex]) {
            this.scrollToService.scrollTo({
              target: 'NAICS_' + this.naicsList[this.naicsSelIndex].CODE,
              container: 'NAICS_AUTOCOMPLETE',
              duration: 650,
              easing: 'easeOutElastic',
              offset: -25 * 5
            });
          }
        }
      }
    }
  }

  /**
   * @description navigate up in dropdown
   * @param event key up event
   * @returns selected naics
   */
  naicsArrowDown(event: any): void {
    event.preventDefault();

    if (this.naicsList) {
      if (this.naicsList.length > 0) {

        // if its first run
        if (this.naicsSelIndex === null) {
          this.naicsSelIndex = 0;
          this.naicsList[0].isOver = true;
          return;
        } else { // Already have something selected
          // Increment Index
          this.naicsSelIndex++;

          // 'Over' NAICS, clear rest
          for (let i = 0; i < this.naicsList.length; i++) {
            this.naicsList[i].isOver = this.naicsSelIndex === i;
          }

          if (this.naicsList[this.naicsSelIndex]) {
            this.scrollToService.scrollTo({
              target: 'NAICS_' + this.naicsList[this.naicsSelIndex].CODE,
              container: 'NAICS_AUTOCOMPLETE',
              duration: 650,
              easing: 'easeOutElastic',
              offset: - 25 * 5
            });
          }
        }
      }
    }
  }

  callUpdateNaicsAPI() {
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'COMPANY';
    objFieldValue.FIELD = 'NAICS';
    objFieldValue.VALUE = this.currentReport.LATEST.COMPANY.NAICS.CODE.toString();
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (res.success) {
        setTimeout(() => {
          document.getElementById('entityType').focus();
          document.activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }, 5);
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }
  //#endregion

  //#region "company-dba"
  /**
   * @description checkbox click for name same for Business Entity Name
   */
  checkNameForSame() {
    this.isSameAsBusinessName = this.currentReport.LATEST.COMPANY.DBA == this.currentReport.LATEST.COMPANY.NAME;
  }

  /** Change values according to selection */
  changeValues(): void {
    if (this.isSameAsBusinessName) {
      this.currentReport.LATEST.COMPANY.DBA = this.currentReport.LATEST.COMPANY.NAME;
    } else {
      this.currentReport.LATEST.COMPANY.DBA = null;
    }
    this.callUpdateAPI('COMPANY', 'DBA');
  }
  //#endregion

  //#region "franchise"
  /**
   * Franchise Selector
   * */
  autocompleteFranchise(value: string): void {
    // Close AutoComplete
    this.franchiseAutoOpen = false;
    this.franchiseError = false;
    // Clear Current Franchise Object
    this.currentReport.LATEST.COMPANY.FRANCHISE = null;
    this.franchiseSelIndex = null;

    if (value) {
      if (value.length > 0) {
        this.reportProvider.getFranchise(1, 100, value).subscribe(
          data => {
            if (data.success) {
              this.franchiseList = data.result.items;
              if (this.franchiseList.length) {
                this.franchiseAutoOpen = true;
              } else {
                this.franchiseError = true;
              }
            }
            else {
              this.franchiseError = true;
            }
          }, err => {
            // Error Handling
          });
      } else this.franchiseList = []
    }
  }

  /**
   * @description for select franchise
   * @param franchise seleted franchise
   */
  selectFranchise(franchise: Franchise): void {
    this.currentReport.LATEST.COMPANY.FRANCHISE = franchise;
    this.currentReport.LATEST.COMPANY.FRANCHISE_NAME = franchise.FRANCHISE_NAME;
    this.franchiseList = [];
    this.franchiseAutoOpen = false;
    this.franchiseError = false;
    this.franchiseSelIndex = null;
    this.callUpdateFranchiseAPI();
  }

  /**
   * for focus lost of dropdown
   */
  franchiseFocusLost(): void {
    setTimeout(() => {
      this.franchiseAutoOpen = false;
    }, 350);
  }

  /**
   * @description for reste franchise
   */
  resetFranchise() {
    this.currentReport.LATEST.COMPANY.FRANCHISE = null;
    this.currentReport.LATEST.COMPANY.FRANCHISE_NAME = '';
    this.franchiseSelIndex = null;
  }

  callUpdateFranchiseAPI() {
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'COMPANY';
    objFieldValue.FIELD = 'FRANCHISE';
    objFieldValue.VALUE = this.currentReport.LATEST.COMPANY.FRANCHISE.FRANCHISE_ID;
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (res.success) {
        setTimeout(() => {
          document.getElementById('businessPurpose').focus();
          document.activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }, 5);
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description on tab press of dropdown
   * @param event tab press event
   */
  franchiseTabPress(event: any) {
    if (this.franchiseList)
      if (this.franchiseList.length > 0) {
        if (this.franchiseSelIndex)
          this.selectFranchise(this.franchiseList[this.franchiseSelIndex]);
        else
          this.selectFranchise(this.franchiseList[0]);
      }
  }

  /**
   * @description for pick naics
   * @param event click event
   */
  pickFranchiseFromList(event: any): void {
    event.preventDefault();
    if (this.franchiseSelIndex !== null && this.franchiseList) {
      if (this.franchiseList[this.franchiseSelIndex]) {
        this.selectFranchise(this.franchiseList[this.franchiseSelIndex]);
      }
    }
  }

  /**
   * @description navigate up in dropdown
   * @param event key up event
   * @returns selected franchise
   */
  franchiseArrowUp(event: any): void {
    event.preventDefault();

    if (this.franchiseList) {
      if (this.franchiseList.length > 0) {

        // If its last upper item, clear
        if (this.franchiseSelIndex === 0) {
          this.franchiseSelIndex = null;
          this.franchiseList[0].isOver = false;
          return;
        }

        // if its not the first run
        if (this.franchiseSelIndex !== null) {
          // Increment Index
          this.franchiseSelIndex--;

          // 'Over' NAICS, clear rest
          for (let i = 0; i < this.franchiseList.length; i++) {
            this.franchiseList[i].isOver = this.franchiseSelIndex === i;
          }

          if (this.franchiseList[this.franchiseSelIndex]) {
            this.scrollToService.scrollTo({
              target: 'FRANCHISE_' + this.franchiseList[this.franchiseSelIndex].FRANCHISE_ID,
              container: 'FRANCHISE_AUTOCOMPLETE',
              duration: 650,
              easing: 'easeOutElastic',
              offset: -25 * 5
            });
          }
        }
      }
    }
  }

  /**
   * @description navigate up in dropdown
   * @param event key up event
   * @returns selected franchise
   */
  franchiseArrowDown(event: any): void {
    event.preventDefault();

    if (this.franchiseList) {
      if (this.franchiseList.length > 0) {

        // if its first run
        if (this.franchiseSelIndex === null) {
          this.franchiseSelIndex = 0;
          this.franchiseList[0].isOver = true;
          return;
        } else { // Already have something selected
          // Increment Index
          this.franchiseSelIndex++;

          // 'Over' NAICS, clear rest
          for (let i = 0; i < this.franchiseList.length; i++) {
            this.franchiseList[i].isOver = this.franchiseSelIndex === i;
          }

          if (this.franchiseList[this.franchiseSelIndex]) {
            this.scrollToService.scrollTo({
              target: 'FRANCHISE_' + this.franchiseList[this.franchiseSelIndex].FRANCHISE_ID,
              container: 'FRANCHISE_AUTOCOMPLETE',
              duration: 650,
              easing: 'easeOutElastic',
              offset: - 25 * 5
            });
          }
        }
      }
    }
  }

  //#endregion

  getQbTooltip(): string {
    return 'All information about this question was added from Quickbooks. To edit this information, you need to start typing new data in the input field. Pay attention after changing information in the input field, updated information from the Quickbooks is no longer pulled up in this field.';
  }

  /**
   * @description function of interface for validation
   * @returns form valid or not
   */
  isFormValid(): boolean {
    return this.overviewForm.valid;
  }

  /**
   * @description function of interface for skip functionality
   * @returns form can be skippable or not
   */
  canBeSkipped(): boolean {
    return false;
  }

  /**
   * @description report footer button click options
   * @param buttonOption which button clicked by user
   */
  footerButtonClick(buttonOption: number) {
    if (buttonOption == FooterButtonOption.NEXT_CLICK) {
      this.reportService.updateMaxStage(PageNumbers.OVERVIEW);
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/location`);
    } else if (buttonOption == FooterButtonOption.BACK_CLICK) {
      this.router.navigateByUrl(`/dashboard`);
    }
  }

}
